import { Link } from "react-router-dom";
import { ArrowRight, ShieldCheck, ShieldAlert, Sparkles } from "lucide-react";
import { Seo } from "@/components/Seo";
import { PageHero } from "@/components/PageHero";
import { MeyarLogo } from "@/components/MeyarLogo";
import { InnerBlock, BulletList } from "@/components/InnerBlock";
import { InnerCTA } from "@/components/InnerCTA";
import { RelatedTiles } from "@/components/RelatedTiles";
import CyberSecurityGRC from "@/pages/inner/meyar/CyberSecurityGRC";
import EnterpriseGRC from "@/pages/inner/meyar/EnterpriseGRC";
import {
  MEYAR_SHARED_FEATURES,
  MEYAR_SPREADSHEET_BREAKS,
  MEYAR_COMPARISON,
  MEYAR_EDITIONS,
  MEYAR_ONBOARDING,
} from "@/data/meyar";

// Both product pages have been rebuilt as standalone components inside
// /pages/inner/meyar/. Re-exporting keeps App.js imports stable.
export const MeyarCyberSecurityGRC = CyberSecurityGRC;
export const MeyarEnterpriseGRC = EnterpriseGRC;

export function MeyarOverview() {
  return (
    <div data-testid="meyar-overview">
      <Seo title="Meyar — GRC platform" description="Meyar (معيار) means standard. Governance, risk and compliance for enterprise and information security in one platform." />
      <PageHero
        eyebrowNode={<MeyarLogo variant="colour" className="h-11 md:h-12" />}
        h1="Meyar — the standard your organisation is measured against."
        intro="Meyar (معيار) means standard or criterion. Most organisations here run governance out of spreadsheets — the risk register in one file, policies in a shared drive, evidence in somebody's inbox. It holds together right up until the auditor walks in."
        breadcrumbs={[{ label: "Home", to: "/" }, { label: "Meyar" }]}
      />

      <InnerBlock tint="cream" heading="The spreadsheet breaks in the same five places">
        <BulletList items={MEYAR_SPREADSHEET_BREAKS} />
      </InnerBlock>

      <InnerBlock tint="white" heading="Choose your line" subheading="Start with one. Add the other when you are ready. They share the same platform, so nothing is rebuilt and nothing is re-entered.">
        <div className="grid gap-6 md:grid-cols-2">
          <Link to="/meyar/enterprise-grc" data-testid="meyar-choose-enterprise" className="group flex flex-col rounded-lg border border-wafa-deep/10 bg-white p-6 transition-all hover:-translate-y-1 hover:border-wafa-deep/25 hover:shadow-[0_18px_40px_-20px_rgba(14,79,58,0.25)]">
            <div className="flex h-11 w-11 items-center justify-center rounded-md bg-wafa-primary text-white"><ShieldCheck className="h-5 w-5" /></div>
            <div className="mt-5 font-heading text-2xl font-semibold text-wafa-deep">Meyar Enterprise GRC</div>
            <p className="mt-3 text-sm text-wafa-ink/75">Enterprise risk, policy, audit and board reporting.</p>
            <div className="mt-6 inline-flex items-center gap-1.5 text-sm font-semibold text-wafa-primary">Read more <ArrowRight className="h-4 w-4" /></div>
          </Link>
          <Link to="/meyar/cyber-security-grc" data-testid="meyar-choose-cyber" className="group flex flex-col rounded-lg border border-wafa-deep/10 bg-white p-6 transition-all hover:-translate-y-1 hover:border-wafa-deep/25 hover:shadow-[0_18px_40px_-20px_rgba(14,79,58,0.25)]">
            <div className="flex h-11 w-11 items-center justify-center rounded-md bg-wafa-deep text-white"><ShieldAlert className="h-5 w-5" /></div>
            <div className="mt-5 font-heading text-2xl font-semibold text-wafa-deep">Meyar Cyber Security GRC</div>
            <p className="mt-3 text-sm text-wafa-ink/75">ISO 27001 and NIST control governance.</p>
            <div className="mt-6 inline-flex items-center gap-1.5 text-sm font-semibold text-wafa-primary">Read more <ArrowRight className="h-4 w-4" /></div>
          </Link>
        </div>
      </InnerBlock>

      <InnerBlock tint="cream" heading="Shared platform features" eyebrow="Both product lines">
        <ul className="grid gap-3">
          {MEYAR_SHARED_FEATURES.map((f, i) => (
            <li key={i} className="flex items-start gap-3 rounded-md border border-wafa-deep/10 bg-white p-4">
              <Sparkles className="mt-0.5 h-4 w-4 shrink-0 text-wafa-primary" />
              <span className="text-sm text-wafa-ink/85 lg:text-base">{f}</span>
            </li>
          ))}
        </ul>
      </InnerBlock>

      <InnerBlock tint="white" heading="How the two lines compare">
        <div className="overflow-x-auto rounded-lg border border-wafa-deep/10">
          <table className="w-full min-w-[720px] text-sm">
            <thead className="bg-wafa-cream/40 text-start text-xs uppercase tracking-wider text-wafa-deep">
              <tr>
                <th className="px-4 py-3"></th>
                <th className="px-4 py-3">Enterprise GRC</th>
                <th className="px-4 py-3">Cyber Security GRC</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-wafa-deep/10">
              {MEYAR_COMPARISON.map((row) => (
                <tr key={row.label}>
                  <td className="px-4 py-3 font-semibold text-wafa-deep">{row.label}</td>
                  <td className="px-4 py-3 text-wafa-ink/80">{row.enterprise}</td>
                  <td className="px-4 py-3 text-wafa-ink/80">{row.cyber}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </InnerBlock>

      <InnerBlock tint="cream" heading="Editions">
        <div className="grid gap-4 md:grid-cols-3">
          {MEYAR_EDITIONS.map((e) => (
            <div key={e.name} className="rounded-lg border border-wafa-deep/10 bg-white p-6">
              <div className="font-heading text-lg font-semibold text-wafa-deep">{e.name}</div>
              <p className="mt-3 text-sm text-wafa-ink/75">{e.body}</p>
            </div>
          ))}
        </div>
        <p className="mt-5 text-xs text-wafa-ink/60">Pricing on request. Every edition includes onboarding and training.</p>
      </InnerBlock>

      <InnerBlock tint="cream" heading="Onboarding is part of the product" subheading="Not a consulting engagement.">
        <ol className="space-y-4 border-s-2 border-wafa-primary ps-5">
          {MEYAR_ONBOARDING.map((step, i) => (
            <li key={i} className="text-sm text-wafa-ink/85 lg:text-base">
              <span className="font-semibold text-wafa-deep">Step {i + 1}. </span>{step}
            </li>
          ))}
        </ol>
        <p className="mt-4 text-sm text-wafa-ink/70">A working risk register can be live in two weeks; a full rollout six to ten.</p>
      </InnerBlock>

      <RelatedTiles heading="Related" items={[
        { tag: "Services", label: "PDPL compliance", to: "/pdpl-compliance" },
        { tag: "Industries", label: "Government & public sector", to: "/industries/government-public-sector-oman" },
        { tag: "Industries", label: "Banking & financial services", to: "/industries/banking-financial-services-oman" },
      ]} />
      <InnerCTA heading="Book a twenty-minute demo." body="See the risk register, the AI assistant and a real board pack. No consulting sell." />
    </div>
  );
}




